import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Navigate, NavLink, Outlet } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Bell,
  BellOff,
  CheckCircle2,
  Database,
  HardDrive,
  History,
  Info,
  Mail,
  Eye,
  Pencil,
  Phone,
  Play,
  Plus,
  RefreshCw,
  RotateCcw,
  UserPlus,
  Users,
  UserCheck,
  X,
  XOctagon,
} from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import type {
  SyncRun,
  PipelineFilesResponse,
  PipelineFile,
  PipelineFileCheck,
  AuditTimeline,
  RestorePreview,
} from '@/lib/types';
import { fmtRelative, fmtDateTimeMty, fmtEpochMty, fmtBytes } from '@/lib/format';
import { cn } from '@/lib/utils';

type UserRole = 'agente' | 'gerente' | 'admin' | 'supervisor' | 'ventas' | 'cobranza';

interface AdminUser {
  id: number;
  email: string;
  nombre: string;
  rol: UserRole;
  /** Rol ventas: comerciales (Sales Rep) cuyas carteras ve (varios = inside). */
  comercialNombres?: string[];
  /** Operativos: Ops Rep (CW) vinculado — el import le asigna esos bookings. */
  opsRepNombre?: string | null;
  /** Tipo de gerente (solo rol gerente): 'operaciones' | 'ventas'. */
  gerenteTipo?: string | null;
  /** Se auto-registró (login Google) y espera que un admin le asigne rol. */
  rolPendiente?: boolean;
  activo: boolean;
  notificacionesEmail: boolean;
  telefono: string | null;
  emailsAlternos: string[];
  notas: string | null;
  avatarUrl: string | null;
  createdAt: string;
  updatedAt: string;
  /**
   * Cuenta protegida: solo el propio root puede editarse.
   * Marcado en backend via ROOT_EMAILS env var.
   */
  isRoot: boolean;
}

interface TeamAssignmentRow {
  id: number;
  agenteId: number;
  gerenteId: number;
  agente: { id: number; email: string; nombre: string; activo: boolean };
  gerente: { id: number; email: string; nombre: string; activo: boolean };
}

type Severity = 'info' | 'warning' | 'error' | 'critical';

interface ErrorLog {
  id: number;
  severity: Severity;
  source: string;
  code: string;
  message: string;
  detail: string | null;
  alertedAt: string | null;
  resolvedAt: string | null;
  resolvedBy: number | null;
  createdAt: string;
}

// ─── Admin layout with sub-tabs ────────────────────────────────────────────
interface AdminTab {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface AdminTabDef extends AdminTab {
  /** Solo visible para cuentas root (ROOT_EMAILS). */
  rootOnly?: boolean;
}

const ADMIN_TABS: AdminTabDef[] = [
  { to: '/admin/equipos', label: 'Equipos y usuarios', icon: Users },
  { to: '/admin/delegaciones', label: 'Delegaciones', icon: UserCheck },
  { to: '/admin/sync', label: 'Sincronización', icon: Database },
  { to: '/admin/monitoreo', label: 'Monitoreo', icon: AlertTriangle },
  { to: '/admin/notificaciones', label: 'Notificaciones', icon: Bell },
  { to: '/admin/pipeline', label: 'Insumos & Pipeline', icon: HardDrive, rootOnly: true },
  { to: '/admin/auditoria', label: 'Auditoría & Restauración', icon: History, rootOnly: true },
];

export function AdminLayout() {
  const { user } = useAuth();
  // gerente: SOLO la pestaña de Delegaciones (cobertura de vacaciones)
  const tabs = ADMIN_TABS.filter(
    (t) =>
      (!t.rootOnly || user?.isRoot) &&
      (user?.rol !== 'gerente' || t.to === '/admin/delegaciones'),
  );
  return (
    <div className="px-3 sm:px-6 py-4 sm:py-6 max-w-6xl mx-auto space-y-4 sm:space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Admin</h1>
        <p className="text-sm text-ink-400 mt-1">Operaciones del sistema</p>
      </div>

      {/* Tab nav — wrappable, sin scroll horizontal */}
      <div className="border-b border-ink-200 -mx-3 sm:-mx-6 px-3 sm:px-6">
        <nav
          className="flex flex-wrap gap-x-1 gap-y-0"
          aria-label="Secciones de admin"
        >
          {tabs.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                cn(
                  'inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-2 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors',
                  isActive
                    ? 'border-brand-500 text-brand-700'
                    : 'border-transparent text-ink-600 hover:text-ink-900 hover:border-ink-300',
                )
              }
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
      </div>

      <Outlet />
    </div>
  );
}

// Página por defecto: redirige a /admin/equipos
export function AdminIndexRedirect() {
  const { user } = useAuth();
  return <Navigate to={user?.rol === 'gerente' ? '/admin/delegaciones' : '/admin/equipos'} replace />;
}

// Exports individuales para cada sub-ruta (todas las secciones se exportan
// nombradas más arriba como componentes locales — re-exportamos para el router).
export { SyncSection, EquiposSection, MonitoringSection, NotificationsSection, PipelineSection, AuditSection };

// ─── Delegaciones (cobertura de vacaciones) ────────────────────────────────
// El delegado ve y trabaja los bookings del operativo mientras la delegación
// esté activa. admin: todos los operativos; gerente: solo su equipo.
interface DelegacionesData {
  agentes: { id: number; nombre: string; delegados: { id: number; nombre: string }[] }[];
  posiblesDelegados: { id: number; nombre: string }[];
}

export function DelegacionesSection() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['delegaciones'],
    queryFn: () => api<DelegacionesData>('/admin/delegaciones'),
  });
  const mut = useMutation({
    mutationFn: (v: { agenteId: number; delegadoId: number | null }) =>
      api(`/admin/delegaciones/${v.agenteId}`, {
        method: 'PATCH',
        body: JSON.stringify({ delegadoId: v.delegadoId }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['delegaciones'] });
      toast.success('Delegación actualizada');
    },
    onError: (err: any) =>
      toast.error(err instanceof ApiError ? err.message : 'No se pudo actualizar'),
  });

  const data = q.data;
  return (
    <div className="space-y-4 max-w-3xl">
      <div>
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <UserCheck className="h-5 w-5" /> Delegaciones por vacaciones
        </h2>
        <p className="text-sm text-ink-400 mt-1">
          Asigna un delegado a un operativo que sale de vacaciones: el delegado podrá ver y
          trabajar sus embarques y tareas pendientes mientras dure la delegación. Al quitarla,
          el acceso se cierra de inmediato.
        </p>
      </div>
      {q.isLoading && <p className="text-sm text-ink-400">Cargando…</p>}
      {data && (
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-ink-400 border-b border-ink-200">
              <th className="py-2 pr-3">Operativo</th>
              <th className="py-2">Sus pendientes los cubre…</th>
            </tr>
          </thead>
          <tbody>
            {data.agentes.map((a) => {
              const delegadoActual = a.delegados[0]?.id;
              return (
                <tr key={a.id} className="border-b border-ink-100">
                  <td className="py-2 pr-3 font-medium">{a.nombre}</td>
                  <td className="py-2">
                    <Select
                      value={delegadoActual != null ? String(delegadoActual) : '__none__'}
                      onValueChange={(v) =>
                        mut.mutate({
                          agenteId: a.id,
                          delegadoId: v === '__none__' ? null : Number(v),
                        })
                      }
                    >
                      <SelectTrigger className="w-72">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— sin delegado —</SelectItem>
                        {data.posiblesDelegados
                          .filter((d) => d.id !== a.id)
                          .map((d) => (
                            <SelectItem key={d.id} value={String(d.id)}>
                              {d.nombre}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    {a.delegados.length > 1 && (
                      <div className="text-xs text-amber-600 mt-1">
                        Tiene {a.delegados.length} delegados: {a.delegados.map((d) => d.nombre).join(', ')}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
            {data.agentes.length === 0 && (
              <tr>
                <td colSpan={2} className="py-6 text-center text-ink-400">
                  No tienes operativos asignados en tu equipo.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Sync ──────────────────────────────────────────────────────────────────
function SyncSection() {
  const qc = useQueryClient();
  const runsQ = useQuery({
    queryKey: ['sync-runs'],
    queryFn: () => api<{ items: SyncRun[]; total: number }>('/admin/sync/runs', { params: { limit: 10 } }),
    refetchInterval: (q) => {
      const running = q.state.data?.items.some((r: SyncRun) => r.estado === 'running');
      return running ? 3000 : 60_000;
    },
  });

  const trigger = useMutation({
    mutationFn: () => api('/admin/sync/trigger', { method: 'POST' }),
    onSuccess: (r: any) => {
      toast.success(
        `Sync OK — ${r.bookingsNuevos ?? 0} bookings nuevos, ${r.itemsNuevos ?? 0} items, ${Math.round((r.durationMs ?? 0) / 1000)}s`,
      );
      qc.invalidateQueries({ queryKey: ['sync-runs'] });
      qc.invalidateQueries({ queryKey: ['bookings'] });
    },
    onError: (err: any) => toast.error(err instanceof ApiError ? err.message : 'Error'),
  });

  return (
    <section className="rounded-lg border border-ink-200 bg-surface-1 p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Database className="h-4 w-4" /> Sincronización
          </h2>
          <p className="text-sm text-ink-400 mt-1">
            Lee del Sheet o del pipeline y actualiza la base de datos.
          </p>
        </div>
        <Button onClick={() => trigger.mutate()} disabled={trigger.isPending}>
          <RefreshCw className={cn('h-4 w-4', trigger.isPending && 'animate-spin')} />
          {trigger.isPending ? 'Sincronizando…' : 'Sync ahora'}
        </Button>
      </div>

      <div className="mt-4 overflow-x-auto rounded-md border border-ink-200">
        <table className="w-full text-sm">
          <thead className="bg-surface-2 text-xs uppercase tracking-wider text-ink-400">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Hora</th>
              <th className="text-left px-3 py-2 font-medium">Trigger</th>
              <th className="text-left px-3 py-2 font-medium">Estado</th>
              <th className="text-right px-3 py-2 font-medium tabular">Filas</th>
              <th className="text-right px-3 py-2 font-medium tabular">Nuevos</th>
              <th className="text-right px-3 py-2 font-medium tabular">Update</th>
              <th className="text-right px-3 py-2 font-medium tabular">Errores</th>
            </tr>
          </thead>
          <tbody>
            {runsQ.data?.items.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-ink-400">Sin syncs</td></tr>
            )}
            {runsQ.data?.items.map((r) => (
              <tr key={r.id} className="border-t border-ink-100">
                <td className="px-3 py-2 text-xs text-ink-400">{fmtRelative(r.iniciadoAt)}</td>
                <td className="px-3 py-2 text-xs">{r.trigger}</td>
                <td className="px-3 py-2">
                  <span className={cn(
                    'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                    r.estado === 'success' && 'bg-emerald-100 text-emerald-700',
                    r.estado === 'running' && 'bg-amber-100 text-amber-700',
                    r.estado === 'failed' && 'bg-brand-100 text-brand-700',
                  )}>{r.estado}</span>
                </td>
                <td className="px-3 py-2 text-right tabular">{r.filasLeidas.toLocaleString()}</td>
                <td className="px-3 py-2 text-right tabular">{r.bookingsNuevos}</td>
                <td className="px-3 py-2 text-right tabular text-ink-400">{r.bookingsUpdate}</td>
                <td className={cn('px-3 py-2 text-right tabular', r.errores > 0 && 'text-brand-500 font-semibold')}>
                  {r.errores}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ─── Monitoring / Error Logs ──────────────────────────────────────────────
function MonitoringSection() {
  const [onlyUnresolved, setOnlyUnresolved] = useState(true);
  const qc = useQueryClient();

  const logsQ = useQuery({
    queryKey: ['error-logs', onlyUnresolved],
    queryFn: () =>
      api<{ items: ErrorLog[]; pagination: any }>('/admin/error-logs', {
        params: { perPage: 30, onlyUnresolved: onlyUnresolved || undefined },
      }),
    refetchInterval: 30_000,
  });

  const resolveMut = useMutation({
    mutationFn: (id: number) => api(`/admin/error-logs/${id}/resolve`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['error-logs'] });
      toast.success('Marcado como resuelto');
    },
    onError: (err: any) => toast.error(err instanceof ApiError ? err.message : 'Error'),
  });

  const summary = (logsQ.data?.items ?? []).reduce(
    (acc, l) => {
      acc[l.severity]++;
      return acc;
    },
    { info: 0, warning: 0, error: 0, critical: 0 } as Record<Severity, number>,
  );

  return (
    <section className="rounded-lg border border-ink-200 bg-surface-1 p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" /> Monitoreo y errores
          </h2>
          <p className="text-sm text-ink-400 mt-1">
            Errores capturados del API, pipeline y health checks. Alertas por correo se envían según severidad.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <SeverityBadge severity="critical" count={summary.critical} />
          <SeverityBadge severity="error" count={summary.error} />
          <SeverityBadge severity="warning" count={summary.warning} />
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <Button
          variant={onlyUnresolved ? 'primary' : 'secondary'}
          size="sm"
          onClick={() => setOnlyUnresolved(true)}
        >
          Sin resolver
        </Button>
        <Button
          variant={!onlyUnresolved ? 'primary' : 'secondary'}
          size="sm"
          onClick={() => setOnlyUnresolved(false)}
        >
          Todos
        </Button>
      </div>

      <div className="mt-4 overflow-x-auto rounded-md border border-ink-200">
        <table className="w-full text-sm">
          <thead className="bg-surface-2 text-xs uppercase tracking-wider text-ink-400">
            <tr>
              <th className="text-left px-3 py-2 font-medium w-28">Hora</th>
              <th className="text-left px-3 py-2 font-medium w-24">Severidad</th>
              <th className="text-left px-3 py-2 font-medium w-32">Origen / Código</th>
              <th className="text-left px-3 py-2 font-medium">Mensaje</th>
              <th className="text-left px-3 py-2 font-medium w-24">Alertado</th>
              <th className="px-3 py-2 font-medium w-20"></th>
            </tr>
          </thead>
          <tbody>
            {logsQ.isLoading && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-ink-400">Cargando…</td></tr>
            )}
            {logsQ.data?.items.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-12 text-center text-emerald-600">
                  <CheckCircle2 className="inline h-4 w-4 mr-1" /> Sin errores registrados
                </td>
              </tr>
            )}
            {logsQ.data?.items.map((l) => (
              <tr
                key={l.id}
                className={cn('border-t border-ink-100', l.resolvedAt && 'opacity-50')}
              >
                <td className="px-3 py-2 text-xs text-ink-400 tabular">{fmtRelative(l.createdAt)}</td>
                <td className="px-3 py-2"><SeverityBadge severity={l.severity} count={1} hideZero={false} compact /></td>
                <td className="px-3 py-2 text-xs">
                  <div className="font-medium">{l.source}</div>
                  <div className="text-ink-400 font-mono">{l.code}</div>
                </td>
                <td className="px-3 py-2">
                  <details>
                    <summary className="cursor-pointer">{l.message}</summary>
                    {l.detail && (
                      <pre className="mt-2 p-2 bg-surface-2 rounded text-xs overflow-x-auto max-h-48">{l.detail}</pre>
                    )}
                  </details>
                </td>
                <td className="px-3 py-2 text-xs text-ink-400">
                  {l.alertedAt ? <Mail className="h-3 w-3 inline text-emerald-600" /> : '—'}
                </td>
                <td className="px-3 py-2 text-right">
                  {!l.resolvedAt && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => resolveMut.mutate(l.id)}
                      disabled={resolveMut.isPending}
                    >
                      Resolver
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SeverityBadge({
  severity,
  count,
  compact,
  hideZero = true,
}: {
  severity: Severity;
  count: number;
  compact?: boolean;
  hideZero?: boolean;
}) {
  if (count === 0 && hideZero) return null;
  const conf = {
    info: { variant: 'default' as const, Icon: Info, label: 'Info' },
    warning: { variant: 'warning' as const, Icon: AlertTriangle, label: 'Aviso' },
    error: { variant: 'danger' as const, Icon: XOctagon, label: 'Error' },
    critical: { variant: 'danger' as const, Icon: XOctagon, label: 'CRÍTICO' },
  }[severity];
  return (
    <Badge variant={conf.variant} className={cn(severity === 'critical' && 'font-bold')}>
      <conf.Icon className="h-3 w-3" />
      {compact ? conf.label : `${conf.label}: ${count}`}
    </Badge>
  );
}

// ─── Equipos / Team management ─────────────────────────────────────────────
function EquiposSection() {
  const qc = useQueryClient();
  const { user: currentUser } = useAuth();
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'todos' | 'activos' | 'inactivos'>(
    'activos',
  );
  // "Ver como" (solo staging): emite un JWT del usuario objetivo y recarga la
  // app con esa sesión; el token admin original se guarda para poder volver.
  async function verComo(u: AdminUser) {
    try {
      const r = await api<{ token: string }>('/auth/impersonate', {
        method: 'POST',
        body: JSON.stringify({ userId: u.id }),
      });
      localStorage.setItem('opsapp_token_original', localStorage.getItem('opsapp_token') ?? '');
      localStorage.setItem('opsapp_token', r.token);
      window.location.href = '/';
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'No se pudo iniciar "Ver como"');
    }
  }

  // Filtro por rol desde los contadores (multi-select; vacío = todos los roles)
  const [rolesSel, setRolesSel] = useState<Set<UserRole>>(new Set());
  const toggleRol = (r: UserRole) =>
    setRolesSel((prev) => {
      const next = new Set(prev);
      if (next.has(r)) next.delete(r);
      else next.add(r);
      return next;
    });
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [creatingUser, setCreatingUser] = useState(false);

  const usersQ = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => api<AdminUser[]>('/admin/users'),
  });

  const teamQ = useQuery({
    queryKey: ['admin-team'],
    queryFn: () => api<TeamAssignmentRow[]>('/admin/team-assignments'),
  });

  const updateRolMut = useMutation({
    mutationFn: ({ id, rol }: { id: number; rol: UserRole }) =>
      api(`/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify({ rol }) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      qc.invalidateQueries({ queryKey: ['admin-team'] });
      toast.success('Rol actualizado');
    },
    onError: (err: any) => toast.error(err instanceof ApiError ? err.message : 'Error'),
  });

  const toggleActivoMut = useMutation({
    mutationFn: ({ id, activo }: { id: number; activo: boolean }) =>
      api(`/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify({ activo }) }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      toast.success(vars.activo ? 'Usuario activado' : 'Usuario desactivado');
    },
    onError: (err: any) => toast.error(err instanceof ApiError ? err.message : 'Error'),
  });

  const toggleNotifMut = useMutation({
    mutationFn: ({ id, notificacionesEmail }: { id: number; notificacionesEmail: boolean }) =>
      api(`/admin/users/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ notificacionesEmail }),
      }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      toast.success(
        vars.notificacionesEmail
          ? 'Notificaciones por correo activadas'
          : 'Notificaciones por correo desactivadas',
      );
    },
    onError: (err: any) => toast.error(err instanceof ApiError ? err.message : 'Error'),
  });

  // Build agent → managers map
  const managersByAgent = useMemo(() => {
    const map = new Map<number, TeamAssignmentRow[]>();
    for (const ta of teamQ.data ?? []) {
      const arr = map.get(ta.agenteId) ?? [];
      arr.push(ta);
      map.set(ta.agenteId, arr);
    }
    return map;
  }, [teamQ.data]);

  // Build gerente → agentes-que-supervisa map (dirección inversa).
  const supervisedByGerente = useMemo(() => {
    const map = new Map<number, TeamAssignmentRow[]>();
    for (const ta of teamQ.data ?? []) {
      const arr = map.get(ta.gerenteId) ?? [];
      arr.push(ta);
      map.set(ta.gerenteId, arr);
    }
    return map;
  }, [teamQ.data]);

  const filtered = useMemo(() => {
    const all = usersQ.data ?? [];
    const q = filter.trim().toLowerCase();
    return all.filter((u) => {
      if (statusFilter === 'activos' && !u.activo) return false;
      if (statusFilter === 'inactivos' && u.activo) return false;
      if (rolesSel.size > 0 && !rolesSel.has(u.rol)) return false;
      if (!q) return true;
      return (
        u.nombre.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        u.rol.includes(q) ||
        (u.telefono ?? '').toLowerCase().includes(q) ||
        u.emailsAlternos.some((e) => e.toLowerCase().includes(q))
      );
    });
  }, [usersQ.data, filter, statusFilter, rolesSel]);

  const stats = useMemo(() => {
    const all = usersQ.data ?? [];
    return {
      total: all.length,
      agentes: all.filter((u) => u.rol === 'agente').length,
      gerentes: all.filter((u) => u.rol === 'gerente').length,
      admins: all.filter((u) => u.rol === 'admin').length,
      supervisores: all.filter((u) => u.rol === 'supervisor').length,
      ventas: all.filter((u) => u.rol === 'ventas').length,
      inactivos: all.filter((u) => !u.activo).length,
      asignaciones: teamQ.data?.length ?? 0,
    };
  }, [usersQ.data, teamQ.data]);

  return (
    <section className="rounded-lg border border-ink-200 bg-surface-1 p-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Users className="h-4 w-4" /> Equipos y usuarios
          </h2>
          <p className="text-sm text-ink-400 mt-1">
            Gestiona roles y la relación agente ↔ gerente. Los cambios se aplican al instante; los
            permisos se evalúan en cada request.
          </p>
        </div>
        <Button onClick={() => setCreatingUser(true)}>
          <UserPlus className="h-4 w-4" />
          Nuevo usuario
        </Button>
      </div>

      {/* Contadores-filtro: click = filtrar la lista por ese tipo (multi-select,
          click de nuevo deselecciona). "Total" limpia los filtros. */}
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <ChipFiltro
          activo={rolesSel.size === 0 && statusFilter === 'todos'}
          onClick={() => {
            setRolesSel(new Set());
            setStatusFilter('todos');
          }}
          title="Quitar filtros (mostrar todos)"
        >
          Total: {stats.total}
        </ChipFiltro>
        <ChipFiltro activo={rolesSel.has('agente')} onClick={() => toggleRol('agente')}>
          Agentes: {stats.agentes}
        </ChipFiltro>
        <ChipFiltro activo={rolesSel.has('gerente')} onClick={() => toggleRol('gerente')}>
          Gerentes: {stats.gerentes}
        </ChipFiltro>
        <ChipFiltro activo={rolesSel.has('admin')} onClick={() => toggleRol('admin')}>
          Admins: {stats.admins}
        </ChipFiltro>
        <ChipFiltro
          activo={rolesSel.has('supervisor')}
          onClick={() => toggleRol('supervisor')}
        >
          Supervisores: {stats.supervisores}
        </ChipFiltro>
        <ChipFiltro activo={rolesSel.has('ventas')} onClick={() => toggleRol('ventas')}>
          Ventas: {stats.ventas}
        </ChipFiltro>
        <ChipFiltro
          activo={statusFilter === 'inactivos'}
          warning
          onClick={() =>
            setStatusFilter((s) => (s === 'inactivos' ? 'activos' : 'inactivos'))
          }
          title="Solo usuarios sin acceso"
        >
          Inactivos: {stats.inactivos}
        </ChipFiltro>
        <Badge variant="default">Asignaciones: {stats.asignaciones}</Badge>
      </div>

      <PendingUsersSection usuarios={usersQ.data ?? []} />

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Input
          placeholder="Buscar nombre, email, teléfono o rol…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="max-w-md"
        />
        <div className="inline-flex rounded-md border border-ink-200 overflow-hidden text-xs">
          {(['activos', 'inactivos', 'todos'] as const).map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={cn(
                'px-3 py-1.5 capitalize',
                statusFilter === s
                  ? 'bg-brand-50 text-brand-700 font-medium'
                  : 'text-ink-600 hover:bg-surface-2',
              )}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 overflow-x-auto rounded-md border border-ink-200">
        <table className="w-full text-sm">
          <thead className="bg-surface-2 text-xs uppercase tracking-wider text-ink-400">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Usuario</th>
              <th className="text-left px-3 py-2 font-medium w-32">Rol</th>
              <th className="text-left px-3 py-2 font-medium w-44">Contacto</th>
              <th className="text-left px-3 py-2 font-medium">Gerentes</th>
              <th className="text-center px-3 py-2 font-medium w-24">Acceso</th>
              <th className="text-center px-3 py-2 font-medium w-28">Notif. correo</th>
              <th className="px-3 py-2 font-medium w-20"></th>
            </tr>
          </thead>
          <tbody>
            {usersQ.isLoading && (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-ink-400">
                  Cargando…
                </td>
              </tr>
            )}
            {!usersQ.isLoading && filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-ink-400">
                  Sin usuarios con estos filtros
                </td>
              </tr>
            )}
            {filtered.map((u) => {
              const managers = managersByAgent.get(u.id) ?? [];
              // Cuenta root: solo el propio root puede editarse a sí mismo.
              const protectedFromMe =
                u.isRoot && currentUser != null && currentUser.id !== u.id;
              return (
                <tr
                  key={u.id}
                  className={cn(
                    'border-t border-ink-100',
                    !u.activo && 'bg-ink-50/50',
                    u.isRoot && 'bg-violet-50/40',
                  )}
                >
                  <td className="px-3 py-2">
                    <div className={cn('font-medium flex items-center gap-2 flex-wrap', !u.activo && 'text-ink-400')}>
                      {u.nombre}
                      {u.isRoot && (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-100 text-violet-700"
                          title="Cuenta root — solo el propio usuario puede editarla"
                        >
                          🛡 root
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-ink-400">{u.email}</div>
                  </td>
                  <td className="px-3 py-2">
                    <Select
                      value={u.rol}
                      onValueChange={(v) =>
                        updateRolMut.mutate({ id: u.id, rol: v as UserRole })
                      }
                      disabled={updateRolMut.isPending || protectedFromMe}
                    >
                      <SelectTrigger
                        className="h-8 text-xs"
                        title={protectedFromMe ? 'Cuenta root — protegida' : undefined}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="agente">agente</SelectItem>
                        <SelectItem value="gerente">gerente</SelectItem>
                        <SelectItem value="admin">admin</SelectItem>
                        <SelectItem value="director">director</SelectItem>
                        <SelectItem value="supervisor">supervisor</SelectItem>
                        <SelectItem value="ventas">ventas</SelectItem>
                        <SelectItem value="cobranza">cobranza</SelectItem>
                      </SelectContent>
                    </Select>
                    {u.rol === 'ventas' && (
                      <div
                        className={cn(
                          'mt-0.5 text-[11px] truncate max-w-[180px]',
                          (u.comercialNombres?.length ?? 0) === 0
                            ? 'text-amber-600'
                            : 'text-ink-400',
                        )}
                        title={
                          u.comercialNombres?.length
                            ? u.comercialNombres.join(', ')
                            : 'Sin comerciales vinculados — asignar en Editar'
                        }
                      >
                        {u.comercialNombres?.length
                          ? u.comercialNombres.join(', ')
                          : '⚠ sin comercial'}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="text-xs space-y-0.5">
                      {u.telefono ? (
                        <div className="flex items-center gap-1 text-ink-700">
                          <Phone className="h-3 w-3 text-ink-400" />
                          <span className="tabular">{u.telefono}</span>
                        </div>
                      ) : (
                        <div className="text-ink-300 italic">sin teléfono</div>
                      )}
                      {u.emailsAlternos.length > 0 && (
                        <div className="text-ink-400">
                          +{u.emailsAlternos.length} email{u.emailsAlternos.length === 1 ? '' : 's'}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    {u.rol === 'gerente' ? (
                      <span className="text-[11px] text-ink-400 italic">
                        gerente{u.gerenteTipo ? ` · ${u.gerenteTipo}` : ''}
                      </span>
                    ) : u.rol === 'agente' || u.rol === 'ventas' ? (
                      managers.length === 0 ? (
                        <span className="text-xs text-ink-400 italic">sin gerente</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {managers.map((m) => (
                            <Badge key={m.id} variant="default">
                              {m.gerente.nombre}
                            </Badge>
                          ))}
                        </div>
                      )
                    ) : (
                      <span className="text-xs text-ink-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <ToggleSwitch
                      checked={u.activo}
                      onCheckedChange={(v) =>
                        toggleActivoMut.mutate({ id: u.id, activo: v })
                      }
                      labelOn="Activo"
                      labelOff="Sin acceso"
                      disabled={toggleActivoMut.isPending || protectedFromMe}
                    />
                  </td>
                  <td className="px-3 py-2 text-center">
                    <ToggleSwitch
                      checked={u.notificacionesEmail}
                      onCheckedChange={(v) =>
                        toggleNotifMut.mutate({ id: u.id, notificacionesEmail: v })
                      }
                      labelOn={<><BellRingIcon /> ON</>}
                      labelOff={<><BellOff className="h-3 w-3" /> OFF</>}
                      disabled={toggleNotifMut.isPending || !u.activo || protectedFromMe}
                    />
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {currentUser?.impersonacion && u.id !== currentUser.id && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="mr-1"
                        onClick={() => verComo(u)}
                        title={`Ver la app exactamente como ${u.nombre} (solo staging)`}
                      >
                        <Eye className="h-3 w-3" />
                        Ver como
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setEditingUser(u)}
                      disabled={protectedFromMe}
                      title={protectedFromMe ? 'Cuenta root — protegida' : undefined}
                    >
                      <Pencil className="h-3 w-3" />
                      Editar
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modal: editor de usuario (detalles + gerentes) */}
      <UserDetailDialog
        user={editingUser}
        onClose={() => setEditingUser(null)}
        allUsers={usersQ.data ?? []}
        assignments={editingUser ? managersByAgent.get(editingUser.id) ?? [] : []}
        supervises={editingUser ? supervisedByGerente.get(editingUser.id) ?? [] : []}
      />

      {/* Modal: nuevo usuario */}
      <CreateUserDialog
        open={creatingUser}
        onClose={() => setCreatingUser(false)}
        existingEmails={(usersQ.data ?? []).map((u) => u.email.toLowerCase())}
      />
    </section>
  );
}

// ─── Toggle switch ─────────────────────────────────────────────────────────
/** Selector genérico de vínculo CW (comercial u operativo) para la ficha del
 *  usuario. value ''=sin vínculo. Muestra a quién está ligado ya cada nombre. */
function VinculoSelect({
  endpoint,
  queryKey,
  value,
  onChange,
  placeholder,
}: {
  endpoint: string;
  queryKey: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  const NONE = '__none__';
  const q = useQuery({
    queryKey: [queryKey],
    queryFn: () =>
      api<{ nombre: string; bookings: number; vinculadoA?: string | null }[]>(endpoint),
    staleTime: 5 * 60 * 1000,
  });
  const opciones = q.data ?? [];
  // El vínculo actual se muestra aunque ya no aparezca en bookings activos.
  const conValor =
    value && !opciones.some((o) => o.nombre === value)
      ? [{ nombre: value, bookings: 0 }, ...opciones]
      : opciones;
  return (
    <Select value={value || NONE} onValueChange={(v) => onChange(v === NONE ? '' : v)}>
      <SelectTrigger className="h-8 mt-1 text-xs">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NONE}>— sin vínculo —</SelectItem>
        {conValor.map((o) => (
          <SelectItem key={o.nombre} value={o.nombre}>
            {o.nombre}
            {o.bookings > 0 ? ` · ${o.bookings}` : ''}
            {o.vinculadoA && o.nombre !== value ? `  (ya ligado a ${o.vinculadoA})` : ''}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** Contador clickeable que actúa como filtro toggle (multi-select) de la lista. */
function ChipFiltro({
  activo,
  warning,
  onClick,
  title,
  children,
}: {
  activo: boolean;
  warning?: boolean;
  onClick: () => void;
  title?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-pressed={activo}
      className={cn(
        'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors cursor-pointer select-none',
        activo
          ? warning
            ? 'border-amber-500 bg-amber-100 text-amber-900 ring-1 ring-amber-400'
            : 'border-brand-500 bg-brand-50 text-brand-700 ring-1 ring-brand-400'
          : warning
            ? 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100'
            : 'border-ink-200 bg-surface-2 text-ink-600 hover:bg-ink-100',
      )}
    >
      {children}
    </button>
  );
}

/** Usuarios auto-registrados esperando rol. El admin les asigna rol aquí
 *  (y si es ventas, su comercial) en una sola acción. */
function PendingUsersSection({ usuarios }: { usuarios: AdminUser[] }) {
  const pendientes = usuarios.filter((u) => u.rolPendiente);
  const qc = useQueryClient();
  // rol elegido (aún no aplicado) por usuario pendiente
  const [seleccion, setSeleccion] = useState<
    Record<number, { rol?: UserRole; comerciales?: string[]; operativo?: string }>
  >({});
  const operativosQ = useQuery({
    queryKey: ['admin-operativos-nombres'],
    queryFn: () =>
      api<{ nombre: string; bookings: number; vinculadoA?: string | null }[]>(
        '/admin/operativos-nombres',
      ),
    staleTime: 5 * 60 * 1000,
    enabled: pendientes.length > 0,
  });

  const asignarMut = useMutation({
    mutationFn: ({
      id,
      rol,
      comerciales,
      operativo,
    }: {
      id: number;
      rol: UserRole;
      comerciales?: string[];
      operativo?: string;
    }) =>
      api(`/admin/users/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          rol,
          ...(comerciales?.length ? { comercialNombres: comerciales } : {}),
          ...(operativo ? { opsRepNombre: operativo } : {}),
        }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      toast.success('Rol asignado — el usuario ya puede entrar');
    },
    onError: (err: any) => toast.error(err instanceof ApiError ? err.message : 'Error'),
  });

  // Rechazar = desactivar sin asignar rol: sale de esta lista y en el login
  // verá "cuenta desactivada, contacta al administrador". Reversible desde la
  // lista de usuarios (filtro Inactivos → interruptor Activo).
  const rechazarMut = useMutation({
    mutationFn: (id: number) =>
      api(`/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify({ activo: false }) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      toast.success('Cuenta desactivada — sin acceso a la aplicación');
    },
    onError: (err: any) => toast.error(err instanceof ApiError ? err.message : 'Error'),
  });

  // Sin pendientes: visible igual (estado vacío discreto) para que el admin
  // sepa que aquí aparecerán los que inicien sesión sin cuenta.
  if (pendientes.length === 0) {
    return (
      <div className="mt-4 rounded-md border border-ink-200 bg-surface-2 px-4 py-2.5 flex items-center gap-2 text-xs text-ink-500">
        <UserPlus className="h-3.5 w-3.5 text-ink-400" />
        <span>
          <b>Pendientes de asignar rol:</b> ninguno.
        </span>
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 p-4">
      <h3 className="text-sm font-semibold text-amber-900 flex items-center gap-2">
        <UserPlus className="h-4 w-4" />
        Pendientes de asignar rol ({pendientes.length})
      </h3>
      <p className="text-xs text-amber-800 mt-0.5">
        Iniciaron sesión con Google pero no pueden entrar hasta que les asignes rol.
        Si es un comercial, elige rol <b>ventas</b> y relaciónalo con su nombre para
        que vea sus bookings.
      </p>
      <div className="mt-3 space-y-2">
        {pendientes.map((u) => {
          const sel = seleccion[u.id] ?? {};
          const esVentas = sel.rol === 'ventas';
          // agente: vínculo operativo OBLIGATORIO (si no, no vería/recibiría
          // sus bookings); gerente: opcional (puede llevar bookings propios).
          const esOperativo = sel.rol === 'agente' || sel.rol === 'gerente';
          const listo =
            !!sel.rol &&
            (!esVentas || (sel.comerciales?.length ?? 0) > 0) &&
            (sel.rol !== 'agente' || !!sel.operativo);
          return (
            <div
              key={u.id}
              className="flex flex-wrap items-center gap-2 rounded border border-amber-200 bg-surface-1 px-3 py-2"
            >
              <div className="min-w-[220px]">
                <div className="text-sm font-medium text-ink-800">{u.nombre}</div>
                <div className="text-xs text-ink-400">
                  {u.email} · registrado {fmtRelative(u.createdAt)}
                </div>
              </div>
              <Select
                value={sel.rol}
                onValueChange={(v) =>
                  setSeleccion((s) => ({ ...s, [u.id]: { ...s[u.id], rol: v as UserRole } }))
                }
              >
                <SelectTrigger className="h-8 w-52 text-xs">
                  <SelectValue placeholder="Elegir rol…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="agente">agente · trabaja sus bookings</SelectItem>
                  <SelectItem value="gerente">gerente · supervisa agentes</SelectItem>
                  <SelectItem value="ventas">ventas · solo lectura (su cartera)</SelectItem>
                  <SelectItem value="cobranza">cobranza · panel CxC de clientes</SelectItem>
                  <SelectItem value="supervisor">supervisor · solo lectura (todo)</SelectItem>
                  <SelectItem value="admin">admin · acceso total</SelectItem>
                  <SelectItem value="director">director · ve y edita todo (sin Admin)</SelectItem>
                </SelectContent>
              </Select>
              {esVentas && (
                <div className="w-72">
                  <ComercialesPicker
                    value={sel.comerciales ?? []}
                    onChange={(v) =>
                      setSeleccion((s) => ({ ...s, [u.id]: { ...s[u.id], comerciales: v } }))
                    }
                  />
                </div>
              )}
              {esOperativo && (
                <Select
                  value={sel.operativo}
                  onValueChange={(v) =>
                    setSeleccion((s) => ({ ...s, [u.id]: { ...s[u.id], operativo: v } }))
                  }
                >
                  <SelectTrigger className="h-8 w-64 text-xs">
                    <SelectValue
                      placeholder={
                        sel.rol === 'agente'
                          ? 'Relacionar con operativo (CW)…'
                          : 'Operativo CW (opcional)…'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {(operativosQ.data ?? []).map((o) => (
                      <SelectItem key={o.nombre} value={o.nombre}>
                        {o.nombre} · {o.bookings} bookings
                        {o.vinculadoA ? `  (ya ligado a ${o.vinculadoA})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Button
                size="sm"
                disabled={!listo || asignarMut.isPending}
                onClick={() =>
                  asignarMut.mutate({
                    id: u.id,
                    rol: sel.rol!,
                    comerciales: sel.comerciales,
                    operativo: sel.operativo,
                  })
                }
              >
                Asignar
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-red-600 border-red-200 hover:bg-red-50"
                disabled={rechazarMut.isPending}
                onClick={() => {
                  if (
                    window.confirm(
                      `¿Desactivar la cuenta de ${u.nombre} (${u.email})? No podrá entrar a la aplicación. Puedes reactivarla después desde la lista (filtro Inactivos).`,
                    )
                  )
                    rechazarMut.mutate(u.id);
                }}
              >
                Rechazar
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Picker multi-comercial (chips + agregar): un usuario ventas puede ver la
 *  cartera de VARIOS comerciales (perfil "inside"). Controlado: value/onChange. */
function ComercialesPicker({
  value,
  onChange,
  disabled,
}: {
  value: string[];
  onChange: (v: string[]) => void;
  disabled?: boolean;
}) {
  const comercialesQ = useQuery({
    queryKey: ['admin-comerciales'],
    queryFn: () => api<{ nombre: string; bookings: number }[]>('/admin/comerciales'),
    staleTime: 5 * 60 * 1000,
  });
  const restantes = (comercialesQ.data ?? []).filter((o) => !value.includes(o.nombre));
  return (
    <div className="mt-1">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1">
          {value.map((n) => (
            <span
              key={n}
              className="inline-flex items-center gap-1 rounded-full bg-brand-50 border border-brand-200 text-brand-700 px-2 py-0.5 text-[11px]"
            >
              {n}
              {!disabled && (
                <button
                  onClick={() => onChange(value.filter((v) => v !== n))}
                  className="hover:text-brand-900 font-bold"
                  title="Quitar"
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}
      <Select
        key={value.length}
        onValueChange={(v) => onChange([...value, v])}
        disabled={disabled}
      >
        <SelectTrigger
          className={cn(
            'h-7 text-[11px]',
            value.length === 0 && 'border-amber-400 text-amber-700',
          )}
          title="Comerciales (Sales Rep) cuyas carteras ve este usuario"
        >
          <SelectValue
            placeholder={value.length === 0 ? 'Asignar comercial(es)…' : '＋ agregar otro…'}
          />
        </SelectTrigger>
        <SelectContent>
          {restantes.map((o) => (
            <SelectItem key={o.nombre} value={o.nombre}>
              {o.nombre} · {o.bookings}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}


function ToggleSwitch({
  checked,
  onCheckedChange,
  labelOn,
  labelOff,
  disabled,
}: {
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  labelOn?: React.ReactNode;
  labelOff?: React.ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium transition-colors',
        checked
          ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
          : 'bg-ink-100 text-ink-500 hover:bg-ink-200',
        disabled && 'opacity-50 cursor-not-allowed',
      )}
    >
      <span
        className={cn(
          'inline-block h-2 w-2 rounded-full',
          checked ? 'bg-emerald-500' : 'bg-ink-400',
        )}
      />
      {checked ? labelOn : labelOff}
    </button>
  );
}

function BellRingIcon() {
  return <Bell className="h-3 w-3" />;
}

// ─── Create user dialog ───────────────────────────────────────────────────
function CreateUserDialog({
  open,
  onClose,
  existingEmails,
}: {
  open: boolean;
  onClose: () => void;
  existingEmails: string[];
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    email: '',
    nombre: '',
    rol: 'admin' as UserRole,
    telefono: '',
    notas: '',
    emailsAlternos: [] as string[],
    newAlt: '',
    activo: true,
    notificacionesEmail: true,
  });

  // Reset al abrir/cerrar
  useEffect(() => {
    if (open) {
      setForm({
        email: '',
        nombre: '',
        rol: 'admin',
        telefono: '',
        notas: '',
        emailsAlternos: [],
        newAlt: '',
        activo: true,
        notificacionesEmail: true,
      });
    }
  }, [open]);

  const createMut = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api<AdminUser>('/admin/users', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      toast.success(`Usuario creado: ${created.email} (${created.rol})`, {
        description: created.activo
          ? 'Está activo y puede iniciar sesión (si el email está autorizado en ALLOWED_EMAILS).'
          : 'Quedó inactivo — actívalo cuando quieras darle acceso.',
        duration: 6000,
      });
      onClose();
    },
    onError: (err: any) => toast.error(err instanceof ApiError ? err.message : 'Error'),
  });

  const emailLc = form.email.trim().toLowerCase();
  const emailValid = emailLc.includes('@') && emailLc.length > 3;
  const emailTaken = emailValid && existingEmails.includes(emailLc);
  const canSubmit = emailValid && !emailTaken && form.nombre.trim().length > 0 && !createMut.isPending;

  const setField = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  const addAlt = () => {
    const e = form.newAlt.trim().toLowerCase();
    if (!e || !e.includes('@')) return toast.error('Email inválido');
    if (e === emailLc) return toast.error('Ese ya es el email principal');
    if (form.emailsAlternos.includes(e)) return toast.error('Ya está en la lista');
    setForm((prev) => ({
      ...prev,
      emailsAlternos: [...prev.emailsAlternos, e],
      newAlt: '',
    }));
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    createMut.mutate({
      email: emailLc,
      nombre: form.nombre.trim(),
      rol: form.rol,
      telefono: form.telefono.trim(),
      notas: form.notas.trim(),
      emailsAlternos: form.emailsAlternos,
      activo: form.activo,
      notificacionesEmail: form.notificacionesEmail,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-4 w-4" />
            Nuevo usuario
          </DialogTitle>
          <DialogDescription>
            Crea un usuario manualmente (no viene del Sheet EquiposMonday ni del pipeline).
            Útil para admins, gerentes externos o pruebas.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-4">
          {/* Email + nombre */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-ink-400 uppercase tracking-wider">
                Email principal *
              </label>
              <Input
                value={form.email}
                onChange={(e) => setField('email', e.target.value)}
                placeholder="nombre@strd.com.mx"
                className={cn(
                  'mt-1',
                  emailTaken && 'border-brand-500 focus:ring-brand-500',
                )}
                autoFocus
              />
              {emailTaken && (
                <p className="text-[11px] text-brand-700 mt-0.5">
                  Ya existe un usuario con ese email
                </p>
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-ink-400 uppercase tracking-wider">
                Nombre *
              </label>
              <Input
                value={form.nombre}
                onChange={(e) => setField('nombre', e.target.value)}
                placeholder="Juan Pérez"
                maxLength={120}
                className="mt-1"
              />
            </div>
          </div>

          {/* Rol + teléfono */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-ink-400 uppercase tracking-wider">
                Rol
              </label>
              <Select
                value={form.rol}
                onValueChange={(v) => setField('rol', v as UserRole)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">admin · acceso total</SelectItem>
                  <SelectItem value="director">director · ve y edita todo (sin Admin)</SelectItem>
                  <SelectItem value="gerente">gerente · supervisa agentes</SelectItem>
                  <SelectItem value="agente">agente · trabaja sus bookings</SelectItem>
                  <SelectItem value="supervisor">supervisor · solo lectura (todo)</SelectItem>
                  <SelectItem value="ventas">ventas · solo lectura (su cartera)</SelectItem>
                  <SelectItem value="cobranza">cobranza · panel CxC de clientes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-ink-400 uppercase tracking-wider">
                Teléfono
              </label>
              <Input
                value={form.telefono}
                onChange={(e) => setField('telefono', e.target.value)}
                placeholder="+52 81 1234 5678"
                maxLength={40}
                className="mt-1"
              />
            </div>
          </div>

          {/* Toggles */}
          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-start gap-3 rounded-md border border-ink-200 p-3 cursor-pointer hover:bg-surface-2">
              <input
                type="checkbox"
                checked={form.activo}
                onChange={(e) => setField('activo', e.target.checked)}
                className="mt-0.5"
              />
              <div className="flex-1 text-xs">
                <div className="text-sm font-medium">Acceso a Atlas</div>
                <p className="text-ink-400 mt-0.5">
                  Si está activo, puede iniciar sesión inmediatamente.
                </p>
              </div>
            </label>
            <label className="flex items-start gap-3 rounded-md border border-ink-200 p-3 cursor-pointer hover:bg-surface-2">
              <input
                type="checkbox"
                checked={form.notificacionesEmail}
                onChange={(e) => setField('notificacionesEmail', e.target.checked)}
                disabled={!form.activo}
                className="mt-0.5"
              />
              <div className="flex-1 text-xs">
                <div className="text-sm font-medium">Notificaciones por correo</div>
                <p className="text-ink-400 mt-0.5">
                  Recibe alertas y digest diario (si está activo).
                </p>
              </div>
            </label>
          </div>

          {/* Emails alternos */}
          <div>
            <label className="text-xs font-medium text-ink-400 uppercase tracking-wider">
              Correos alternos · acceso compartido
            </label>
            <div className="text-[11px] text-ink-400 mb-1 leading-snug">
              Reciben notificaciones de este usuario y, si tienen cuenta propia, pueden
              ver/editar sus bookings (útil para cobertura por vacaciones).
            </div>
            {form.emailsAlternos.length > 0 && (
              <div className="space-y-1 mb-2">
                {form.emailsAlternos.map((e) => (
                  <div
                    key={e}
                    className="flex items-center justify-between gap-2 rounded-md border border-ink-200 px-3 py-1.5 text-sm"
                  >
                    <span className="flex items-center gap-2">
                      <Mail className="h-3 w-3 text-ink-400" />
                      {e}
                    </span>
                    <button
                      onClick={() =>
                        setForm((prev) => ({
                          ...prev,
                          emailsAlternos: prev.emailsAlternos.filter((x) => x !== e),
                        }))
                      }
                      className="text-ink-400 hover:text-brand-500"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2">
              <Input
                value={form.newAlt}
                onChange={(e) => setField('newAlt', e.target.value)}
                placeholder="alterno@strd.com.mx"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addAlt();
                  }
                }}
              />
              <Button variant="secondary" size="sm" onClick={addAlt}>
                <Plus className="h-3 w-3" />
                Agregar
              </Button>
            </div>
          </div>

          {/* Notas */}
          <div>
            <label className="text-xs font-medium text-ink-400 uppercase tracking-wider">
              Notas internas
            </label>
            <textarea
              value={form.notas}
              onChange={(e) => setField('notas', e.target.value)}
              placeholder="Observaciones, contexto, etc. (solo lo ve el admin)"
              rows={2}
              maxLength={2000}
              className="mt-1 w-full rounded-md border border-ink-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>

          {/* Aviso allowlist si rol=admin */}
          <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
            <strong>Importante:</strong> para que el usuario pueda iniciar sesión, su email
            debe estar dentro del dominio permitido (
            <code className="bg-amber-100 px-1 rounded">ALLOWED_DOMAINS</code>) y, si está
            configurado el modo restringido, también en{' '}
            <code className="bg-amber-100 px-1 rounded">ALLOWED_EMAILS</code> del
            <code className="bg-amber-100 px-1 rounded ml-1">.env</code> del servidor. El
            admin de infraestructura debe agregarlo ahí si no está. Los admins listados en{' '}
            <code className="bg-amber-100 px-1 rounded">ADMIN_EMAILS</code> tienen pase libre.
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {createMut.isPending ? 'Creando…' : 'Crear usuario'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Selector de agentes que un gerente supervisa (dirección inversa del team).
 *  Filtra candidatos por tipo: operaciones→rol agente, ventas→rol ventas. */
function SupervisaPicker({
  gerenteId,
  tipo,
  supervises,
  allUsers,
  onAdd,
  onRemove,
  busy,
  guardarPrimero,
}: {
  gerenteId: number;
  tipo: 'operaciones' | 'ventas';
  supervises: TeamAssignmentRow[];
  allUsers: AdminUser[];
  onAdd: (agenteId: number) => void;
  onRemove: (taId: number) => void;
  busy: boolean;
  guardarPrimero: boolean;
}) {
  const [picked, setPicked] = useState('');
  const rolEsperado = tipo === 'ventas' ? 'ventas' : 'agente';
  const yaAsignados = new Set(supervises.map((a) => a.agenteId));
  const candidatos = allUsers.filter(
    (u) => u.rol === rolEsperado && u.activo && u.id !== gerenteId && !yaAsignados.has(u.id),
  );

  return (
    <div>
      <div className="text-xs font-medium text-ink-400 uppercase tracking-wider mb-1">
        Agentes que supervisa ({supervises.length})
      </div>
      {supervises.length === 0 ? (
        <div className="text-sm text-ink-400 italic mb-2">Sin agentes asignados</div>
      ) : (
        <div className="space-y-1.5 mb-2">
          {supervises.map((a) => (
            <div
              key={a.id}
              className="flex items-center justify-between gap-2 rounded-md border border-ink-200 px-3 py-1.5 text-sm"
            >
              <div>
                <div className="font-medium">{a.agente.nombre}</div>
                <div className="text-xs text-ink-400">{a.agente.email}</div>
              </div>
              <Button size="sm" variant="secondary" onClick={() => onRemove(a.id)} disabled={busy}>
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}
      {guardarPrimero ? (
        <p className="text-[11px] text-amber-600">
          Guarda primero los cambios de gerente para poder asignarle agentes.
        </p>
      ) : (
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <Select value={picked} onValueChange={setPicked}>
              <SelectTrigger className="h-9 text-xs">
                <SelectValue
                  placeholder={`Agregar ${tipo === 'ventas' ? 'comercial' : 'operativo'}…`}
                />
              </SelectTrigger>
              <SelectContent>
                {candidatos.length === 0 && (
                  <div className="px-3 py-2 text-sm text-ink-400 italic">
                    No hay {tipo === 'ventas' ? 'comerciales' : 'operativos'} disponibles
                  </div>
                )}
                {candidatos.map((u) => (
                  <SelectItem key={u.id} value={String(u.id)}>
                    {u.nombre} ({u.email})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="secondary"
            onClick={() => {
              if (!picked) return;
              onAdd(Number(picked));
              setPicked('');
            }}
            disabled={!picked || busy}
          >
            <Plus className="h-4 w-4" />
            Agregar
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── User detail dialog ────────────────────────────────────────────────────
function UserDetailDialog({
  user,
  onClose,
  allUsers,
  assignments,
  supervises,
}: {
  user: AdminUser | null;
  onClose: () => void;
  allUsers: AdminUser[];
  assignments: TeamAssignmentRow[];
  /** Agentes que este usuario supervisa (si es gerente). */
  supervises: TeamAssignmentRow[];
}) {
  const qc = useQueryClient();

  // Estado local del form (se reinicia cuando cambia el usuario abierto)
  const [form, setForm] = useState<{
    nombre: string;
    telefono: string;
    notas: string;
    emailsAlternos: string[];
    newAltEmail: string;
    activo: boolean;
    notificacionesEmail: boolean;
    comercialNombres: string[];
    opsRepNombre: string;
    email: string;
    rol: UserRole;
    gerenteTipo: string; // '' | 'operaciones' | 'ventas'
  } | null>(null);
  const [pickedGerenteId, setPickedGerenteId] = useState<string>('');

  useEffect(() => {
    if (user) {
      setForm({
        nombre: user.nombre,
        telefono: user.telefono ?? '',
        notas: user.notas ?? '',
        emailsAlternos: [...user.emailsAlternos],
        newAltEmail: '',
        activo: user.activo,
        notificacionesEmail: user.notificacionesEmail,
        comercialNombres: [...(user.comercialNombres ?? [])],
        opsRepNombre: user.opsRepNombre ?? '',
        email: user.email,
        rol: user.rol,
        gerenteTipo: user.gerenteTipo ?? '',
      });
      setPickedGerenteId('');
    } else {
      setForm(null);
    }
  }, [user]);

  const saveMut = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api(`/admin/users/${user!.id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      toast.success('Cambios guardados');
      onClose();
    },
    onError: (err: any) => toast.error(err instanceof ApiError ? err.message : 'Error'),
  });

  const addManagerMut = useMutation({
    mutationFn: ({ agenteId, gerenteId }: { agenteId: number; gerenteId: number }) =>
      api('/admin/team-assignments', {
        method: 'POST',
        body: JSON.stringify({ agenteId, gerenteId }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-team'] });
      setPickedGerenteId('');
      toast.success('Gerente asignado');
    },
    onError: (err: any) => toast.error(err instanceof ApiError ? err.message : 'Error'),
  });

  const delManagerMut = useMutation({
    mutationFn: (taId: number) =>
      api(`/admin/team-assignments/${taId}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-team'] });
      toast.success('Gerente desasignado');
    },
    onError: (err: any) => toast.error(err instanceof ApiError ? err.message : 'Error'),
  });

  const availableGerentes = useMemo(() => {
    if (!user) return [];
    const assignedIds = new Set(assignments.map((a) => a.gerenteId));
    return allUsers.filter(
      (u) =>
        (u.rol === 'gerente' || u.rol === 'admin') &&
        u.activo &&
        u.id !== user.id &&
        !assignedIds.has(u.id),
    );
  }, [allUsers, assignments, user]);

  if (!user || !form) {
    return (
      <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
        <DialogContent />
      </Dialog>
    );
  }

  const setField = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((prev) => (prev ? { ...prev, [k]: v } : prev));

  const handleAddAltEmail = () => {
    const e = form.newAltEmail.trim().toLowerCase();
    if (!e || !e.includes('@')) {
      toast.error('Email inválido');
      return;
    }
    if (e === user.email.toLowerCase()) {
      toast.error('Ese ya es el email principal');
      return;
    }
    if (form.emailsAlternos.map((x) => x.toLowerCase()).includes(e)) {
      toast.error('Ya está en la lista');
      return;
    }
    setForm((prev) =>
      prev
        ? {
            ...prev,
            emailsAlternos: [...prev.emailsAlternos, e],
            newAltEmail: '',
          }
        : prev,
    );
  };

  const handleRemoveAltEmail = (e: string) =>
    setForm((prev) =>
      prev ? { ...prev, emailsAlternos: prev.emailsAlternos.filter((x) => x !== e) } : prev,
    );

  const handleSave = () => {
    saveMut.mutate({
      nombre: form.nombre.trim() || user.nombre,
      telefono: form.telefono.trim(),
      notas: form.notas.trim(),
      emailsAlternos: form.emailsAlternos,
      activo: form.activo,
      notificacionesEmail: form.notificacionesEmail,
      comercialNombres: form.comercialNombres,
      opsRepNombre: form.opsRepNombre,
      // rol solo si cambió por la checkbox "Es gerente"; gerenteTipo siempre.
      ...(form.rol !== user.rol ? { rol: form.rol } : {}),
      gerenteTipo: form.rol === 'gerente' ? form.gerenteTipo : '',
      // Solo se manda si cambió (el backend valida dominio/unicidad/root)
      ...(form.email.trim().toLowerCase() !== user.email.toLowerCase()
        ? { email: form.email.trim().toLowerCase() }
        : {}),
    });
  };

  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{user.nombre}</DialogTitle>
          <DialogDescription>
            {user.email} · {user.rol}
            {!user.activo && (
              <span className="ml-2 text-amber-600">· sin acceso a Atlas</span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-5">
          {/* Acceso y notificaciones */}
          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-start gap-3 rounded-md border border-ink-200 p-3 cursor-pointer hover:bg-surface-2">
              <input
                type="checkbox"
                checked={form.activo}
                onChange={(e) => setField('activo', e.target.checked)}
                className="mt-0.5"
              />
              <div className="flex-1">
                <div className="text-sm font-medium">Acceso a Atlas</div>
                <div className="text-xs text-ink-400 mt-0.5">
                  {form.activo
                    ? 'Puede iniciar sesión y ver/editar lo que su rol permite.'
                    : 'No puede iniciar sesión, aunque su email esté en allowlist.'}
                </div>
              </div>
            </label>
            <label className="flex items-start gap-3 rounded-md border border-ink-200 p-3 cursor-pointer hover:bg-surface-2">
              <input
                type="checkbox"
                checked={form.notificacionesEmail}
                onChange={(e) => setField('notificacionesEmail', e.target.checked)}
                disabled={!form.activo}
                className="mt-0.5"
              />
              <div className="flex-1">
                <div className="text-sm font-medium">Notificaciones por correo</div>
                <div className="text-xs text-ink-400 mt-0.5">
                  Recibe alertas del sistema (errores, eventos críticos). Solo aplica si está
                  activo.
                </div>
              </div>
            </label>
          </div>

          {/* Datos básicos */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-ink-400 uppercase tracking-wider">
                Nombre
              </label>
              <Input
                value={form.nombre}
                onChange={(e) => setField('nombre', e.target.value)}
                className="mt-1"
                maxLength={120}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-ink-400 uppercase tracking-wider">
                Correo de acceso (Google)
              </label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setField('email', e.target.value)}
                className={cn(
                  'mt-1',
                  form.email.trim().toLowerCase() !== user.email.toLowerCase() &&
                    'border-amber-400',
                )}
                maxLength={120}
              />
              {form.email.trim().toLowerCase() !== user.email.toLowerCase() && (
                <p className="text-[11px] text-amber-700 mt-0.5">
                  ⚠ Cambiará el correo con el que inicia sesión; conserva sus
                  bookings, vínculos e historial.
                </p>
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-ink-400 uppercase tracking-wider">
                Teléfono
              </label>
              <Input
                value={form.telefono}
                onChange={(e) => setField('telefono', e.target.value)}
                placeholder="+52 81 1234 5678"
                className="mt-1"
                maxLength={40}
              />
            </div>
          </div>

          {/* Vínculos con CargoWise (por si se asignó mal: editable aquí) */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-ink-400 uppercase tracking-wider">
                Vínculo operativo (Ops Rep CW)
              </label>
              <VinculoSelect
                endpoint="/admin/operativos-nombres"
                queryKey="admin-operativos-nombres"
                value={form.opsRepNombre}
                onChange={(v) => setField('opsRepNombre', v)}
                placeholder="Sin vínculo operativo"
              />
              <p className="text-[11px] text-ink-400 mt-1">
                Sus bookings se le asignan por este nombre. Al guardar, se
                reasignan de inmediato.
              </p>
            </div>
            <div>
              <label className="text-xs font-medium text-ink-400 uppercase tracking-wider">
                Vínculos comerciales (Sales Rep CW)
              </label>
              <ComercialesPicker
                value={form.comercialNombres}
                onChange={(v) => setField('comercialNombres', v)}
              />
              <p className="text-[11px] text-ink-400 mt-1">
                Solo aplica al rol ventas: define qué carteras ve. Varios
                comerciales = perfil inside (asistente comercial).
              </p>
            </div>
          </div>

          {/* Emails alternos */}
          <div>
            <label className="text-xs font-medium text-ink-400 uppercase tracking-wider">
              Correos alternos · acceso compartido
            </label>
            <div className="text-[11px] text-ink-400 mb-2 leading-snug">
              Cada correo aquí registrado: <strong>(1)</strong> recibe las notificaciones
              de este usuario, y <strong>(2)</strong> si su dueño tiene cuenta propia en
              Atlas, puede <strong>ver y editar los bookings de este usuario</strong>{' '}
              (útil para cobertura por vacaciones). Quita el correo cuando termine la
              cobertura.
            </div>
            <div className="space-y-1.5">
              {form.emailsAlternos.length === 0 && (
                <div className="text-sm text-ink-400 italic">Sin correos alternos</div>
              )}
              {form.emailsAlternos.map((e) => (
                <div
                  key={e}
                  className="flex items-center justify-between gap-2 rounded-md border border-ink-200 px-3 py-1.5 text-sm"
                >
                  <span className="flex items-center gap-2">
                    <Mail className="h-3 w-3 text-ink-400" />
                    {e}
                  </span>
                  <button
                    onClick={() => handleRemoveAltEmail(e)}
                    className="text-ink-400 hover:text-brand-500"
                    aria-label="Quitar"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
            <div className="mt-2 flex items-center gap-2">
              <Input
                value={form.newAltEmail}
                onChange={(e) => setField('newAltEmail', e.target.value)}
                placeholder="alterno@strd.com.mx"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddAltEmail();
                  }
                }}
              />
              <Button variant="secondary" size="sm" onClick={handleAddAltEmail}>
                <Plus className="h-3 w-3" />
                Agregar
              </Button>
            </div>
          </div>

          {/* Notas */}
          <div>
            <label className="text-xs font-medium text-ink-400 uppercase tracking-wider">
              Notas internas
            </label>
            <textarea
              value={form.notas}
              onChange={(e) => setField('notas', e.target.value)}
              placeholder="Observaciones, contexto, etc. (solo lo ve el admin)"
              rows={3}
              maxLength={2000}
              className="mt-1 w-full rounded-md border border-ink-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>

          {/* ¿Es gerente? checkbox + tipo + agentes que supervisa */}
          <div className="pt-3 border-t border-ink-100">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.rol === 'gerente'}
                onChange={(e) =>
                  setField('rol', e.target.checked ? 'gerente' : ('agente' as UserRole))
                }
              />
              <span className="text-sm font-medium">Es gerente</span>
              <span className="text-xs text-ink-400">
                (supervisa a un equipo de agentes)
              </span>
            </label>

            {form.rol === 'gerente' && (
              <div className="mt-3 pl-6 space-y-3">
                <div>
                  <label className="text-xs font-medium text-ink-400 uppercase tracking-wider">
                    Tipo de gerente
                  </label>
                  <Select
                    value={form.gerenteTipo || '__none__'}
                    onValueChange={(v) => setField('gerenteTipo', v === '__none__' ? '' : v)}
                  >
                    <SelectTrigger className="h-8 mt-1 w-56 text-xs">
                      <SelectValue placeholder="Elegir tipo…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="operaciones">Operaciones (supervisa operativos)</SelectItem>
                      <SelectItem value="ventas">Ventas (supervisa comerciales)</SelectItem>
                    </SelectContent>
                  </Select>
                  {!form.gerenteTipo && (
                    <p className="text-[11px] text-amber-600 mt-1">
                      Elige el tipo para poder asignarle agentes.
                    </p>
                  )}
                </div>

                {form.gerenteTipo && (
                  <SupervisaPicker
                    gerenteId={user.id}
                    tipo={form.gerenteTipo as 'operaciones' | 'ventas'}
                    supervises={supervises}
                    allUsers={allUsers}
                    onAdd={(agenteId) => addManagerMut.mutate({ agenteId, gerenteId: user.id })}
                    onRemove={(taId) => delManagerMut.mutate(taId)}
                    busy={addManagerMut.isPending || delManagerMut.isPending}
                    guardarPrimero={form.rol !== user.rol || (user.gerenteTipo ?? '') !== form.gerenteTipo}
                  />
                )}
              </div>
            )}
          </div>

          {/* Gerentes de este usuario (dirección inversa; para agentes/ventas) */}
          {(user.rol === 'agente' || user.rol === 'ventas') && (
            <div className="pt-3 border-t border-ink-100">
              <div className="text-xs font-medium text-ink-400 uppercase tracking-wider mb-2">
                Gerentes asignados ({assignments.length})
              </div>
              {assignments.length === 0 ? (
                <div className="text-sm text-ink-400 italic mb-2">Sin gerentes asignados</div>
              ) : (
                <div className="space-y-1.5 mb-2">
                  {assignments.map((a) => (
                    <div
                      key={a.id}
                      className="flex items-center justify-between gap-2 rounded-md border border-ink-200 px-3 py-1.5 text-sm"
                    >
                      <div>
                        <div className="font-medium">{a.gerente.nombre}</div>
                        <div className="text-xs text-ink-400">{a.gerente.email}</div>
                      </div>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => delManagerMut.mutate(a.id)}
                        disabled={delManagerMut.isPending}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <Select value={pickedGerenteId} onValueChange={setPickedGerenteId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Asignar nuevo gerente…" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableGerentes.length === 0 && (
                        <div className="px-3 py-2 text-sm text-ink-400 italic">
                          No hay gerentes disponibles
                        </div>
                      )}
                      {availableGerentes.map((g) => (
                        <SelectItem key={g.id} value={String(g.id)}>
                          {g.nombre} ({g.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  variant="secondary"
                  onClick={() => {
                    if (!pickedGerenteId) return;
                    addManagerMut.mutate({
                      agenteId: user.id,
                      gerenteId: Number(pickedGerenteId),
                    });
                  }}
                  disabled={!pickedGerenteId || addManagerMut.isPending}
                >
                  <Plus className="h-4 w-4" />
                  Agregar
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saveMut.isPending}>
            {saveMut.isPending ? 'Guardando…' : 'Guardar cambios'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Notifications ─────────────────────────────────────────────────────────
function NotificationsSection() {
  const testMut = useMutation({
    mutationFn: () => api<{ sent: boolean; reason?: string }>('/admin/notifications/test', { method: 'POST' }),
    onSuccess: (r) => {
      if (r.sent) toast.success('Correo de prueba enviado');
      else toast.error(`No se envió: ${r.reason ?? 'desconocido'}`);
    },
    onError: (err: any) => toast.error(err instanceof ApiError ? err.message : 'Error'),
  });

  const digestMut = useMutation({
    mutationFn: () =>
      api<{
        agentDigestsSent: number;
        gerenteDigestsSent: number;
        totalRecipients: number;
        skipped: string[];
      }>('/admin/notifications/digest', {
        method: 'POST',
        body: JSON.stringify({}),
      }),
    onSuccess: (r) => {
      if (r.agentDigestsSent + r.gerenteDigestsSent === 0) {
        toast.message('No hay tareas vencidas hoy — ningún correo enviado', {
          description: r.skipped.length ? `Skipped: ${r.skipped.length}` : undefined,
        });
      } else {
        toast.success(
          `Digest enviado: ${r.agentDigestsSent} agente${r.agentDigestsSent === 1 ? '' : 's'} + ${r.gerenteDigestsSent} gerente${r.gerenteDigestsSent === 1 ? '' : 's'}`,
          {
            description: `${r.totalRecipients} destinatarios totales` +
              (r.skipped.length ? ` · ${r.skipped.length} skipped` : ''),
          },
        );
      }
    },
    onError: (err: any) => toast.error(err instanceof ApiError ? err.message : 'Error'),
  });

  return (
    <section className="rounded-lg border border-ink-200 bg-surface-1 p-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Bell className="h-4 w-4" /> Notificaciones
          </h2>
          <p className="text-sm text-ink-400 mt-1">
            Alertas del sistema (SMTP). El <strong>digest diario de tareas vencidas</strong> se
            envía automáticamente a las <strong>08:00 CDMX</strong> a agentes y gerentes con
            <span className="px-1.5">acceso activo + notificaciones ON</span>.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="secondary"
            onClick={() => testMut.mutate()}
            disabled={testMut.isPending}
          >
            <Mail className="h-4 w-4" />
            {testMut.isPending ? 'Enviando…' : 'Correo de prueba'}
          </Button>
          <Button
            onClick={() => digestMut.mutate()}
            disabled={digestMut.isPending}
            title="Envía AHORA el digest diario, sin esperar al cron de 08:00"
          >
            <Mail className="h-4 w-4" />
            {digestMut.isPending ? 'Enviando…' : 'Enviar digest ahora'}
          </Button>
        </div>
      </div>
    </section>
  );
}

// ─── Insumos & Pipeline (solo root) ─────────────────────────────────────────
const GRUPO_LABEL: Record<string, string> = {
  'importador-directo': 'Bot correo · requerido',
  'importador-opcional': 'Bot correo · opcional',
  'importador-normalizar': 'Bot correo · norm.',
  datalimpia: 'DataLimpia',
  estatico: 'Estático · manual',
};

function FileStatusBadge({ status }: { status: PipelineFile['status'] }) {
  if (status === 'ok')
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700">
        <CheckCircle2 className="h-3 w-3" /> OK
      </span>
    );
  if (status === 'stale')
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
        <AlertTriangle className="h-3 w-3" /> Viejo
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-brand-100 text-brand-700">
      <XOctagon className="h-3 w-3" /> Falta
    </span>
  );
}

function PipelineSection() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const filesQ = useQuery({
    queryKey: ['pipeline-files'],
    queryFn: () => api<PipelineFilesResponse>('/admin/pipeline/files'),
    enabled: !!user?.isRoot,
    refetchInterval: (q) => (q.state.data?.running ? 3000 : 60_000),
  });

  const runsQ = useQuery({
    queryKey: ['pipeline-runs'],
    queryFn: () => api<SyncRun[]>('/admin/pipeline/runs', { params: { limit: 15 } }),
    enabled: !!user?.isRoot,
    refetchInterval: (q) => {
      const running = Array.isArray(q.state.data)
        ? q.state.data.some((r) => r.estado === 'running')
        : false;
      return running ? 3000 : 60_000;
    },
  });

  const historyQ = useQuery({
    queryKey: ['pipeline-file-history'],
    queryFn: () => api<PipelineFileCheck[]>('/admin/pipeline/file-history', { params: { limit: 15 } }),
    enabled: !!user?.isRoot,
  });

  const runMut = useMutation({
    mutationFn: () => api<{ started: boolean; running?: boolean }>('/admin/pipeline/run', { method: 'POST' }),
    onSuccess: (r) => {
      if (r.started) {
        toast.success('Pipeline disparado — la corrida tarda ~1 min. El estado se actualiza solo.');
      } else if (r.running) {
        toast.message('Ya hay una corrida en progreso', {
          description: 'Espera a que termine antes de disparar otra.',
        });
      }
      // Refrescar en unos segundos cuando ya haya avanzado
      setTimeout(() => {
        qc.invalidateQueries({ queryKey: ['pipeline-files'] });
        qc.invalidateQueries({ queryKey: ['pipeline-runs'] });
        qc.invalidateQueries({ queryKey: ['pipeline-file-history'] });
        qc.invalidateQueries({ queryKey: ['bookings'] });
      }, 4000);
    },
    onError: (err: any) => toast.error(err instanceof ApiError ? err.message : 'Error'),
  });

  // Copia el estado de PROD (avances de operativos incluidos) a staging (show).
  const refreshStagingMut = useMutation({
    mutationFn: () =>
      api<{ ok: boolean; running?: boolean; resumen?: string[] }>(
        '/admin/pipeline/refresh-staging',
        { method: 'POST' },
      ),
    onSuccess: (r) => {
      if (r.ok) {
        toast.success('Staging actualizado con los datos de producción', {
          description: (r.resumen ?? []).join(' · ') || undefined,
          duration: 8000,
        });
      } else if (r.running) {
        toast.message('Ya hay un refresh en progreso', {
          description: 'Espera a que termine (~1 min).',
        });
      }
    },
    onError: (err: any) => toast.error(err instanceof ApiError ? err.message : 'Error'),
  });

  const confirmRefreshStaging = () => {
    const ok = window.confirm(
      'Esto copia el estado ACTUAL de producción (incluidos los avances de los operativos) ' +
        'al ambiente de prueba show.strd.com.mx.\n\n' +
        'Los cambios de prueba hechos en staging se reemplazan. Tarda ~1 minuto.\n\n¿Continuar?',
    );
    if (ok) refreshStagingMut.mutate();
  };

  if (!user?.isRoot) {
    return (
      <section className="rounded-lg border border-ink-200 bg-surface-1 p-8 text-center text-ink-400">
        <HardDrive className="h-6 w-6 mx-auto mb-2 opacity-50" />
        Esta sección requiere privilegios root.
      </section>
    );
  }

  const files = filesQ.data?.files ?? [];
  const summary = files.reduce(
    (acc, f) => {
      acc[f.status]++;
      return acc;
    },
    { ok: 0, stale: 0, missing: 0 } as Record<PipelineFile['status'], number>,
  );
  const running = filesQ.data?.running || runsQ.data?.some((r) => r.estado === 'running');

  return (
    <div className="space-y-5">
      {/* Disparador + resumen */}
      <section className="rounded-lg border border-ink-200 bg-surface-1 p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <HardDrive className="h-4 w-4" /> Insumos & Pipeline
            </h2>
            <p className="text-sm text-ink-400 mt-1">
              Corre el pipeline de Cargowise (XLS → bookings) a mano y vigila que los
              archivos de origen estén llegando. Horas en hora Monterrey.
            </p>
          </div>
          <Button onClick={() => runMut.mutate()} disabled={runMut.isPending || running}>
            <Play className={cn('h-4 w-4', running && 'animate-pulse')} />
            {running ? 'Corriendo…' : runMut.isPending ? 'Disparando…' : 'Correr pipeline ahora'}
          </Button>
        </div>

        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <Badge variant="default">OK: {summary.ok}</Badge>
          {summary.stale > 0 && <Badge variant="warning">Viejos: {summary.stale}</Badge>}
          {summary.missing > 0 && <Badge variant="danger">Faltan: {summary.missing}</Badge>}
          {filesQ.data?.checkedAt && (
            <span className="text-xs text-ink-400">
              Revisado {fmtDateTimeMty(filesQ.data.checkedAt)}
            </span>
          )}
        </div>
      </section>

      {/* Ambiente de prueba (show.strd.com.mx) */}
      <section className="rounded-lg border border-brand-200 bg-brand-50/40 p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-base font-semibold flex items-center gap-2">
              <RefreshCw className="h-4 w-4" /> Ambiente de prueba (show.strd.com.mx)
            </h3>
            <p className="text-sm text-ink-500 mt-1 max-w-xl">
              Copia el estado actual de <strong>producción</strong> —incluidos los avances de
              los operativos (tareas palomeadas, notas, cambios de estado)— al ambiente de
              prueba. Tarda ~1 minuto; los datos de prueba en show se reemplazan.
            </p>
          </div>
          <Button
            variant="secondary"
            onClick={confirmRefreshStaging}
            disabled={refreshStagingMut.isPending}
          >
            <RefreshCw className={cn('h-4 w-4', refreshStagingMut.isPending && 'animate-spin')} />
            {refreshStagingMut.isPending ? 'Actualizando test…' : 'Pasar datos a test'}
          </Button>
        </div>
      </section>

      {/* Estado de insumos en vivo */}
      <section className="rounded-lg border border-ink-200 bg-surface-1 p-5">
        <h3 className="text-sm font-semibold text-ink-600 uppercase tracking-wider mb-3">
          Estado de archivos (en vivo)
        </h3>
        <div className="overflow-x-auto rounded-md border border-ink-200">
          <table className="w-full text-sm">
            <thead className="bg-surface-2 text-xs uppercase tracking-wider text-ink-400">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Archivo</th>
                <th className="text-left px-3 py-2 font-medium w-40">Origen</th>
                <th className="text-left px-3 py-2 font-medium w-24">Estado</th>
                <th className="text-right px-3 py-2 font-medium w-24">Tamaño</th>
                <th className="text-left px-3 py-2 font-medium w-36">Fecha origen</th>
                <th className="text-left px-3 py-2 font-medium w-36">En pipeline (raw)</th>
              </tr>
            </thead>
            <tbody>
              {filesQ.isLoading && (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-ink-400">Cargando…</td></tr>
              )}
              {filesQ.isError && (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-brand-600">
                  No se pudo contactar al contenedor pipeline.
                </td></tr>
              )}
              {!filesQ.isLoading && !filesQ.isError && filesQ.data?.available === false && (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-ink-400">
                  Estado no disponible en este momento (el pipeline no respondió). Reintentando…
                </td></tr>
              )}
              {files.map((f) => (
                <tr key={f.nombre} className={cn('border-t border-ink-100', f.status === 'missing' && 'bg-brand-50/40', f.status === 'stale' && 'bg-amber-50/40')}>
                  <td className="px-3 py-2">
                    <div className="font-medium break-all">{f.nombre}</div>
                    {!f.requerido && <span className="text-[10px] text-ink-400">opcional</span>}
                  </td>
                  <td className="px-3 py-2 text-xs text-ink-500">{GRUPO_LABEL[f.grupo] ?? f.grupo}</td>
                  <td className="px-3 py-2"><FileStatusBadge status={f.status} /></td>
                  <td className="px-3 py-2 text-right tabular text-xs">{fmtBytes(f.raw.sizeBytes ?? f.origen.sizeBytes)}</td>
                  <td className="px-3 py-2 text-xs text-ink-500">{f.grupo === 'estatico' ? '—' : fmtEpochMty(f.origen.mtime)}</td>
                  <td className="px-3 py-2 text-xs text-ink-500">{fmtEpochMty(f.raw.mtime)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Historial de corridas */}
      <section className="rounded-lg border border-ink-200 bg-surface-1 p-5">
        <h3 className="text-sm font-semibold text-ink-600 uppercase tracking-wider mb-3">
          Corridas recientes del pipeline
        </h3>
        <div className="overflow-x-auto rounded-md border border-ink-200">
          <table className="w-full text-sm">
            <thead className="bg-surface-2 text-xs uppercase tracking-wider text-ink-400">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Hora (MTY)</th>
                <th className="text-left px-3 py-2 font-medium">Trigger</th>
                <th className="text-left px-3 py-2 font-medium">Estado</th>
                <th className="text-right px-3 py-2 font-medium">Filas</th>
                <th className="text-right px-3 py-2 font-medium">Nuevos</th>
                <th className="text-right px-3 py-2 font-medium">Update</th>
                <th className="text-right px-3 py-2 font-medium">Errores</th>
              </tr>
            </thead>
            <tbody>
              {runsQ.data?.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-ink-400">Sin corridas</td></tr>
              )}
              {runsQ.data?.map((r) => (
                <tr key={r.id} className="border-t border-ink-100">
                  <td className="px-3 py-2 text-xs text-ink-500" title={fmtRelative(r.iniciadoAt)}>{fmtDateTimeMty(r.iniciadoAt)}</td>
                  <td className="px-3 py-2 text-xs">{r.trigger}</td>
                  <td className="px-3 py-2">
                    <span className={cn(
                      'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                      r.estado === 'success' && 'bg-emerald-100 text-emerald-700',
                      r.estado === 'running' && 'bg-amber-100 text-amber-700',
                      r.estado === 'failed' && 'bg-brand-100 text-brand-700',
                    )}>{r.estado}</span>
                  </td>
                  <td className="px-3 py-2 text-right tabular">{r.filasLeidas.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right tabular">{r.bookingsNuevos}</td>
                  <td className="px-3 py-2 text-right tabular text-ink-400">{r.bookingsUpdate}</td>
                  <td className={cn('px-3 py-2 text-right tabular', r.errores > 0 && 'text-brand-500 font-semibold')}>{r.errores}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Registro de revisiones de insumos */}
        {historyQ.data && historyQ.data.length > 0 && (
          <details className="mt-4">
            <summary className="cursor-pointer text-xs font-medium text-ink-500 uppercase tracking-wider">
              Registro de revisiones de insumos ({historyQ.data.length})
            </summary>
            <div className="mt-2 space-y-1">
              {historyQ.data.map((h) => (
                <div key={h.id} className="flex items-center gap-3 text-xs text-ink-500 border-t border-ink-100 py-1.5">
                  <span className="w-32 tabular">{fmtDateTimeMty(h.checkedAt)}</span>
                  <span className="w-16">{h.trigger}</span>
                  <span className="text-emerald-600">{h.okCount} OK</span>
                  {h.staleCount > 0 && <span className="text-amber-600">{h.staleCount} viejos</span>}
                  {h.missingCount > 0 && <span className="text-brand-600">{h.missingCount} faltan</span>}
                </div>
              ))}
            </div>
          </details>
        )}
      </section>
    </div>
  );
}

// ─── Auditoría & Restauración (solo root) ──────────────────────────────────
function toLocalInput(d: Date): string {
  // Date → 'YYYY-MM-DDTHH:mm' en hora local (para <input type=datetime-local>)
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

function AuditSection() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [from, setFrom] = useState(() => toLocalInput(new Date(Date.now() - 24 * 3600 * 1000)));
  const [to, setTo] = useState(() => toLocalInput(new Date()));
  const [estado, setEstado] = useState<string>('');
  const [userId, setUserId] = useState<number | undefined>(undefined);
  const [preview, setPreview] = useState<RestorePreview | null>(null);

  const filterParams = () => ({
    from: new Date(from).toISOString(),
    to: new Date(to).toISOString(),
    ...(estado ? { estadoNuevo: estado } : {}),
    ...(userId != null ? { userId } : {}),
  });

  const timelineQ = useQuery({
    queryKey: ['audit-timeline', from, to, estado, userId],
    queryFn: () => api<AuditTimeline>('/admin/audit/timeline', { params: { ...filterParams(), limit: 100 } }),
    enabled: !!user?.isRoot && !!from && !!to,
  });

  const previewMut = useMutation({
    mutationFn: () => api<RestorePreview>('/admin/audit/restore/preview', { method: 'POST', body: JSON.stringify(filterParams()) }),
    onSuccess: (r) => setPreview(r),
    onError: (err: any) => toast.error(err instanceof ApiError ? err.message : 'Error'),
  });

  const applyMut = useMutation({
    mutationFn: () => api<{ revertidas: number; bookingsReactivados: number }>('/admin/audit/restore/apply', { method: 'POST', body: JSON.stringify(filterParams()) }),
    onSuccess: (r) => {
      toast.success(`Restauradas ${r.revertidas} tareas · ${r.bookingsReactivados} embarques reactivados`);
      setPreview(null);
      qc.invalidateQueries({ queryKey: ['audit-timeline'] });
      qc.invalidateQueries({ queryKey: ['bookings'] });
    },
    onError: (err: any) => toast.error(err instanceof ApiError ? err.message : 'Error'),
  });

  if (!user?.isRoot) {
    return (
      <section className="rounded-lg border border-ink-200 bg-surface-1 p-8 text-center text-ink-400">
        <History className="h-6 w-6 mx-auto mb-2 opacity-50" />
        Esta sección requiere privilegios root.
      </section>
    );
  }

  const confirmApply = () => {
    if (!preview) return;
    const ok = window.confirm(
      `¿Restaurar ${preview.tareas} tareas (afecta ${preview.bookings} embarques) a su estado previo?\n\nEsta acción revierte los cambios del rango seleccionado que NO hayan sido re-tocados después. Es auditada y reversible.`,
    );
    if (ok) applyMut.mutate();
  };

  return (
    <div className="space-y-5">
      {/* Filtros */}
      <section className="rounded-lg border border-ink-200 bg-surface-1 p-5">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <History className="h-4 w-4" /> Auditoría & Restauración
        </h2>
        <p className="text-sm text-ink-400 mt-1">
          Log de cambios de estado de tareas. Filtra un rango y, si algo salió mal,
          restaura las tareas a su estado previo (con previsualización). Horas en hora Monterrey.
        </p>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <label className="text-xs text-ink-500">
            Desde
            <input type="datetime-local" value={from} onChange={(e) => { setFrom(e.target.value); setPreview(null); }}
              className="mt-1 block rounded-md border border-ink-200 px-2 py-1.5 text-sm" />
          </label>
          <label className="text-xs text-ink-500">
            Hasta
            <input type="datetime-local" value={to} onChange={(e) => { setTo(e.target.value); setPreview(null); }}
              className="mt-1 block rounded-md border border-ink-200 px-2 py-1.5 text-sm" />
          </label>
          <label className="text-xs text-ink-500">
            Estado destino
            <select value={estado} onChange={(e) => { setEstado(e.target.value); setPreview(null); }}
              className="mt-1 block rounded-md border border-ink-200 px-2 py-1.5 text-sm">
              <option value="">todos</option>
              <option value="completado">completado</option>
              <option value="en_proceso">en_proceso</option>
              <option value="sin_iniciar">sin_iniciar</option>
              <option value="detenido">detenido</option>
            </select>
          </label>
          {userId != null && (
            <button onClick={() => { setUserId(undefined); setPreview(null); }}
              className="text-xs text-brand-600 underline">quitar filtro de usuario</button>
          )}
          <Button variant="secondary" size="sm" onClick={() => previewMut.mutate()} disabled={previewMut.isPending}>
            <RotateCcw className="h-4 w-4" /> {previewMut.isPending ? 'Calculando…' : 'Previsualizar restauración'}
          </Button>
        </div>

        {preview && (
          <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-4">
            <div className="text-sm text-amber-900">
              Se restaurarían <strong>{preview.tareas}</strong> tareas en{' '}
              <strong>{preview.bookings}</strong> embarques.
              {Object.keys(preview.porDestino).length > 0 && (
                <span className="text-xs text-amber-700 ml-1">
                  (destino: {Object.entries(preview.porDestino).map(([k, v]) => `${v}→${k}`).join(', ')})
                </span>
              )}
            </div>
            {preview.tareas > 0 && (
              <Button className="mt-3" onClick={confirmApply} disabled={applyMut.isPending}>
                <RotateCcw className={cn('h-4 w-4', applyMut.isPending && 'animate-spin')} />
                {applyMut.isPending ? 'Restaurando…' : `Restaurar ${preview.tareas} tareas`}
              </Button>
            )}
          </div>
        )}
      </section>

      {/* Resumen por usuario */}
      {timelineQ.data && timelineQ.data.porUsuario.length > 0 && (
        <section className="rounded-lg border border-ink-200 bg-surface-1 p-5">
          <h3 className="text-sm font-semibold text-ink-600 uppercase tracking-wider mb-3">Actividad por usuario (en el rango)</h3>
          <div className="flex flex-wrap gap-2">
            {timelineQ.data.porUsuario.map((u) => (
              <button key={u.userId} onClick={() => { setUserId(u.userId); setPreview(null); }}
                className={cn('text-xs rounded-md border px-2.5 py-1.5 hover:bg-surface-2',
                  userId === u.userId ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-ink-200 text-ink-600')}>
                {u.email ?? `id ${u.userId}`}: <strong>{u.cambios.toLocaleString()}</strong> cambios · {u.operaciones} ops
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Timeline de operaciones */}
      <section className="rounded-lg border border-ink-200 bg-surface-1 p-5">
        <h3 className="text-sm font-semibold text-ink-600 uppercase tracking-wider mb-3">Operaciones de cambio de estado</h3>
        <div className="overflow-x-auto rounded-md border border-ink-200">
          <table className="w-full text-sm">
            <thead className="bg-surface-2 text-xs uppercase tracking-wider text-ink-400">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Hora (MTY)</th>
                <th className="text-left px-3 py-2 font-medium">Usuario</th>
                <th className="text-left px-3 py-2 font-medium">Origen</th>
                <th className="text-left px-3 py-2 font-medium">Destino</th>
                <th className="text-right px-3 py-2 font-medium">Tareas</th>
                <th className="text-right px-3 py-2 font-medium">Embarques</th>
              </tr>
            </thead>
            <tbody>
              {timelineQ.isLoading && <tr><td colSpan={6} className="px-3 py-8 text-center text-ink-400">Cargando…</td></tr>}
              {timelineQ.data?.operaciones.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-ink-400">Sin operaciones en el rango</td></tr>}
              {timelineQ.data?.operaciones.map((op) => (
                <tr key={op.opKey} className={cn('border-t border-ink-100', op.count >= 100 && 'bg-amber-50/40', op.source === 'reversion' && 'bg-emerald-50/40')}>
                  <td className="px-3 py-2 text-xs text-ink-500">{fmtDateTimeMty(op.changedAt)}</td>
                  <td className="px-3 py-2 text-xs">{op.email ?? `id ${op.userId}`}</td>
                  <td className="px-3 py-2 text-xs">
                    <span className={cn('inline-block px-1.5 py-0.5 rounded text-[10px]',
                      op.source === 'reversion' ? 'bg-emerald-100 text-emerald-700' : 'bg-ink-100 text-ink-600')}>{op.source}</span>
                  </td>
                  <td className="px-3 py-2 text-xs">{op.estadoNuevo}</td>
                  <td className={cn('px-3 py-2 text-right tabular', op.count >= 100 && 'font-semibold text-amber-700')}>{op.count.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right tabular text-ink-500">{op.bookings}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
